import _get from "lodash/get";
import _truncate from "lodash/truncate";
import PropTypes from "prop-types";
import * as qs from "query-string";
import React from "react";
import ReactHighcharts from "react-highcharts";

import { history } from "../../store";
import styles from "../../styles/components/CompareDriversGraph.css";

export const CompareDriversGraphLegend = () => (
    <div className={styles.benchmarkKey}>
        <div className={styles.benchmarkDash} />
        <p className={styles.benchmarkLabel}>Industry benchmark</p>
    </div>
);

function makeSeriesForPage(page, teamId, driverLabels) {
    const { pageLabel, color } = page;

    return {
        name: `Page ${pageLabel}`,
        color: color,
        data: driverLabels.map(driver => ({
            y: Math.round(_get(page.drivers, driver, 0)),
            pageName: _truncate(page.name, { length: 30 }),
            color: color,
            label: pageLabel
        })),
        dataLabels: {
            enabled: true,
            inside: true,
            color: "white",
            verticalAlign: "bottom",
            format: pageLabel,
            borderWidth: 0,
            style: { textOutline: null }
        },
        cursor: "pointer",
        borderWidth: 0,
        edgeWidth: 0,
        states: { hover: { brightness: 0.1 } },
        events: {
            mouseOut: function() {
                // reset hover state of the reset of the columns in the series
                for (const data of this.data) {
                    data.setState("");
                }
            },
            click: function(event) {
                // link to driver page
                const driver = event.point.category;
                const pathname = `/team/${teamId}/results/${
                    page.wevoInfoId
                }/drivers`;
                const queryString = qs.stringify({ selected: page.id, driver });
                return history.push(`${pathname}?${queryString}`);
            }
        }
    };
}

function makeGraphConfig(teamId, pages, driverBenchmarks) {
    const driverLabels = Object.keys(driverBenchmarks);
    const benchmarkScores = Object.values(driverBenchmarks).map(
        ({ avg }) => avg
    );

    return {
        chart: {
            type: "column",
            height: 270
        },
        credits: { enabled: false },
        legend: { enabled: false },
        title: { text: "" },
        series: [
            ...pages.map(page => makeSeriesForPage(page, teamId, driverLabels)),
            ...benchmarkScores.map((score, index) => ({
                type: "line",
                name: driverLabels[index],
                // Each "category" on the x-axis starts at x = index and has width = 1. All of the columns fit in that
                // interval. If we plot a point at x = index, it will be centered on the columns. This plots each
                // benchmark as a series of 2 points spanning 70% of the category.
                data: [[index - 0.35, score], [index + 0.35, score]],
                color: "black",
                enableMouseTracking: false,
                // Disable the marker to hide the points. The line connecting them will still appear.
                marker: { enabled: false }
            }))
        ],
        xAxis: {
            categories: driverLabels,
            // The benchmark series force a wider range on the x-axis than we want. Setting min & max puts it back
            // where we want it.
            min: 0,
            max: driverLabels.length - 1
        },
        yAxis: {
            title: { text: "" },
            labels: { format: "{value}" }
        },
        tooltip: {
            useHTML: true,
            outside: true,
            formatter: function() {
                // highlight the rest of the columns in the series by updating their hover state
                for (const data of this.series.data) {
                    data.setState("hover");
                }

                // this isn't very beautiful, but custom tooltips require an HTML string, and don't accept objects
                return `
                    <b style="color: ${this.point.color}">${
                    this.series.name
                }</b>
                    <br/>
                    <b>${this.point.pageName}</b>
                    <br/>
                    ${this.x} Score: ${this.y}
                `;
            }
        },
        plotOptions: {
            series: {
                animation: false
            }
        }
    };
}

export default function CompareDriversGraph({
    teamId,
    pages,
    driverBenchmarks
}) {
    const config = makeGraphConfig(teamId, pages, driverBenchmarks);
    return (
        <div>
            <ReactHighcharts config={config} />
        </div>
    );
}

CompareDriversGraph.propTypes = {
    teamId: PropTypes.number.isRequired,
    pages: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            color: PropTypes.string.isRequired,
            pageLabel: PropTypes.string.isRequired,
            drivers: PropTypes.object.isRequired
        })
    ).isRequired,
    driverBenchmarks: PropTypes.object.isRequired
};



// WEBPACK FOOTER //
// ./src/components/Results/CompareDriversGraph.js